import * as Progress from "@radix-ui/react-progress";
import React, { type HTMLAttributes, useEffect } from "react";
import { animated, useSpring } from "react-spring";
import { tv, type VariantProps } from "tailwind-variants";
import { cn } from "../../../util/classNameConstructor";

const ProgressBarStyles = tv({
  slots: {
    bar: "bg-background-default text-action-primary-default mx-auto h-1.5 w-full overflow-hidden",
    indicator: "h-full w-[var(--width)] bg-current",
  },
  variants: {
    round: {
      true: {
        bar: "rounded-full",
        indicator: "rounded-full",
      },
      false: {
        bar: "rounded-none",
        indicator: "rounded-none",
      },
    },
  },
});
interface ProgressBarProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof ProgressBarStyles> {
  value: number;
  "aria-label"?: string;
}

const Root = animated(Progress.Root);
const Indicator = animated(Progress.Indicator);

const ProgressBar = ({
  value,
  round,
  className,
  "aria-label": ariaLabel,
}: ProgressBarProps) => {
  const { bar, indicator } = ProgressBarStyles({ round });

  const [{ val }, api] = useSpring(() => ({ val: 0 }));

  // Round the value to 1 decimal place for better screen reader experience
  const roundedValue = Math.round(value * 10) / 10;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    api.start({
      val: value,
      // immediate: true,
      config: { tension: 500, friction: 50 },
    });
  }, [value]);

  return (
    <Root
      className={cn(bar({ className }))}
      value={roundedValue}
      style={{ "--width": val.to((v) => `${Math.max(v, 0)}%`) }}
      aria-live="polite"
      aria-label={ariaLabel || "progress"}
    >
      <Indicator className={indicator()} />
    </Root>
  );
};

export default ProgressBar;
